import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import Swal from 'sweetalert2';
import { useQuery } from '@tanstack/react-query';
import './GiftCardImage.css';

const SS_KEYS = {
    language:       'gci-language',
    page:           'gci-page',
    size:           'gci-size',
    tagFilter:      'gci-tag-filter',
    scrollY:        'gci-scroll-y',
    fromEdit:       'gci-from-edit',
    editedIds:      'gci-edited-ids',
    recentlyViewed: 'gci-recently-viewed',
    lastEdited:     'gci-last-edited',
};

function ssGet(key, fallback) {
    try { const v = sessionStorage.getItem(key); return v !== null ? v : fallback; } catch { return fallback; }
}
function ssSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch {}
}
function ssRemove(key) {
    try { sessionStorage.removeItem(key); } catch {}
}
function getEditedIds() {
    try { return new Set(JSON.parse(sessionStorage.getItem(SS_KEYS.editedIds) || '[]')); } catch { return new Set(); }
}
function getRecentlyViewed() {
    try { return JSON.parse(sessionStorage.getItem(SS_KEYS.recentlyViewed) || 'null'); } catch { return null; }
}
function getLastEdited() {
    try { return JSON.parse(sessionStorage.getItem(SS_KEYS.lastEdited) || 'null'); } catch { return null; }
}

// Deterministic color per slug so each tag always shows the same colour
const TAG_PALETTE = [
    { bg: '#F5EEFF', text: '#7B4B9E', border: '#D4B8F0' },
    { bg: '#FFF8E5', text: '#AA7000', border: '#FFD97A' },
    { bg: '#E8F8F0', text: '#2D7D56', border: '#98DDB8' },
    { bg: '#FFF0F1', text: '#CC3344', border: '#F5A3AB' },
    { bg: '#E8F4FE', text: '#1A6FA8', border: '#90CCF0' },
    { bg: '#FFF4E8', text: '#A85200', border: '#FFBF7A' },
    { bg: '#F0EFFF', text: '#4A44AA', border: '#B8B6F0' },
    { bg: '#F0FFF8', text: '#1A7A5A', border: '#8AE0C0' },
];

function slugColor(slug) {
    let h = 0;
    for (let i = 0; i < slug.length; i++) h = slug.charCodeAt(i) + ((h << 5) - h);
    return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
}

function TagChip({ slug, label }) {
    const c = slugColor(slug);
    return (
        <span
            className="gci-tag"
            style={{ background: c.bg, color: c.text, borderColor: c.border }}
        >
            {label}
        </span>
    );
}

function StatusSwitch({ checked, onChange }) {
    return (
        <label className="gci-switch" title={checked ? 'Active — click to deactivate' : 'Inactive — click to activate'}>
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="gci-slider" />
        </label>
    );
}

export default function GiftCardImage() {
    const axiosPrivate = useAxiosPrivate();
    const [selectedLanguage, setSelectedLanguage] = useState(() => ssGet(SS_KEYS.language, 'da'));
    const [data, setData] = useState([]);
    const [page, setPage] = useState(() => Number(ssGet(SS_KEYS.page, '1')));
    const [size, setSize] = useState(() => Number(ssGet(SS_KEYS.size, '20')));
    const [tagFilter, setTagFilter] = useState(() => ssGet(SS_KEYS.tagFilter, ''));
    const [totalRecords, setTotalRecords] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [editingPositionId, setEditingPositionId] = useState(null);
    const [positionInput, setPositionInput] = useState('');
    const [editedIds, setEditedIds] = useState(() => getEditedIds());
    const [recentlyViewed, setRecentlyViewed] = useState(() => getRecentlyViewed());
    const [lastEdited, setLastEdited] = useState(() => getLastEdited());
    const scrollRestoredRef = useRef(false);
    const pendingScrollRef = useRef(null);

    const swal = Swal.mixin({
        customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-danger me-3' },
        buttonsStyling: false,
    });

    // Persist filter state to sessionStorage
    useEffect(() => { ssSet(SS_KEYS.language, selectedLanguage); }, [selectedLanguage]);
    useEffect(() => { ssSet(SS_KEYS.page, String(page)); }, [page]);
    useEffect(() => { ssSet(SS_KEYS.size, String(size)); }, [size]);
    useEffect(() => { ssSet(SS_KEYS.tagFilter, tagFilter); }, [tagFilter]);

    // Load categories once for tag-label lookup
    useEffect(() => {
        axiosPrivate.get('gift-category/all')
            .then(res => { if (res.data?.data) setCategories(res.data.data); })
            .catch(() => {});
    }, [axiosPrivate]);

    // Derive selected category ID whenever tagFilter or categories change
    useEffect(() => {
        if (!tagFilter || categories.length === 0) { setSelectedCategoryId(null); return; }
        const cat = categories.find(c => c.slug === tagFilter);
        setSelectedCategoryId(cat?.id ?? null);
    }, [tagFilter, categories]);

    const handleTagFilter = useCallback((slug) => {
        setTagFilter(prev => prev === slug ? '' : slug);
        setPage(1);
    }, []);

    const fetchGiftCards = async () => {
        const effectiveSize = size === 0 ? 9999 : size;
        let url = `gift-image/all?language_code=${selectedLanguage}&page=${page}&size=${effectiveSize}`;
        if (tagFilter) url += `&tag=${encodeURIComponent(tagFilter)}`;
        const res = await axiosPrivate.get(url);
        return res.data;
    };

    const { data: queryResult, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['get-gift-cards', selectedLanguage, page, size, tagFilter],
        queryFn: fetchGiftCards,
        keepPreviousData: true,
        cacheTime: 5000,
    });

    useEffect(() => {
        if (queryResult) {
            setData(queryResult.data || []);
            setTotalRecords(queryResult.total || 0);
            setLastPage(queryResult.last_page || 1);
        }
    }, [queryResult]);

    // Restore scroll & refresh edited-ID set when returning from an edit
    // Must be after useQuery so isLoading is in scope
    useEffect(() => {
        if (scrollRestoredRef.current || isLoading || data.length === 0) return;
        if (ssGet(SS_KEYS.fromEdit, '') !== 'true') return;
        const savedY = Number(ssGet(SS_KEYS.scrollY, '0'));
        ssRemove(SS_KEYS.fromEdit);
        ssRemove(SS_KEYS.scrollY);
        scrollRestoredRef.current = true;
        setEditedIds(getEditedIds());
        setRecentlyViewed(getRecentlyViewed());
        setLastEdited(getLastEdited());
        if (savedY > 0) setTimeout(() => window.scrollTo({ top: savedY, behavior: 'instant' }), 60);
    }, [isLoading, data]);

    // Fire pending scroll after filters cleared + data reloaded
    useEffect(() => {
        if (!pendingScrollRef.current || isLoading || data.length === 0) return;
        const id = pendingScrollRef.current;
        setTimeout(() => {
            const el = document.querySelector(`[data-card-id="${id}"]`);
            if (!el) return;
            pendingScrollRef.current = null;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.remove('gci-highlight-pulse');
            void el.offsetWidth;
            el.classList.add('gci-highlight-pulse');
            setTimeout(() => el.classList.remove('gci-highlight-pulse'), 2000);
        }, 80);
    }, [isLoading, data]);

    const handleToggleStatus = useCallback(async (id, value) => {
        await axiosPrivate.put(`gift-image/is_active/${id}`, { is_active: value });
        refetch();
    }, [axiosPrivate, refetch]);

    const handleDelete = useCallback(async (id) => {
        const result = await swal.fire({
            title: 'Delete this gift card?',
            text: 'This cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        });
        if (result.isConfirmed) {
            try {
                const res = await axiosPrivate.delete(`gift-image/delete/${id}`);
                if (res.status === 200) {
                    swal.fire('Deleted', 'Gift card has been removed.', 'success');
                    refetch();
                }
            } catch {
                swal.fire('Error', 'Could not delete the card.', 'error');
            }
        }
    }, [axiosPrivate, refetch, swal]);

    const handleEditClick = useCallback((row) => {
        ssSet(SS_KEYS.scrollY, String(window.scrollY));
        ssSet(SS_KEYS.fromEdit, 'true');
        ssSet(SS_KEYS.recentlyViewed, JSON.stringify({ id: row.id, title: row.title, image: row.image }));
    }, []);

    const handleDismissRecentlyViewed = useCallback(() => {
        ssRemove(SS_KEYS.recentlyViewed);
        setRecentlyViewed(null);
    }, []);

    const fireHighlight = useCallback((id) => {
        const el = document.querySelector(`[data-card-id="${id}"]`);
        if (!el) return false;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.remove('gci-highlight-pulse');
        void el.offsetWidth;
        el.classList.add('gci-highlight-pulse');
        setTimeout(() => el.classList.remove('gci-highlight-pulse'), 2000);
        return true;
    }, []);

    const jumpToCard = useCallback((id) => {
        if (fireHighlight(id)) return;
        // Card not in DOM — clear filters and switch to All, then retry after reload
        pendingScrollRef.current = id;
        setTagFilter('');
        setSize(0);
        setPage(1);
    }, [fireHighlight]);

    const handleScrollToRecentlyViewed = useCallback(() => {
        if (recentlyViewed) jumpToCard(recentlyViewed.id);
    }, [recentlyViewed, jumpToCard]);

    const handleClearHistory = useCallback(() => {
        ssRemove(SS_KEYS.editedIds);
        ssRemove(SS_KEYS.lastEdited);
        setEditedIds(new Set());
        setLastEdited(null);
    }, []);

    const handleScrollToLastEdited = useCallback(() => {
        if (lastEdited) jumpToCard(lastEdited.id);
    }, [lastEdited, jumpToCard]);

    // ── Drag-and-drop ───────────────────────────────────────────
    const handleDragStart = useCallback((e, id) => {
        setDraggingId(id);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e, id) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (id !== draggingId) setDragOverId(id);
    }, [draggingId]);

    const handleDrop = useCallback(async (e, targetId) => {
        e.preventDefault();
        setDragOverId(null);
        if (!draggingId || draggingId === targetId) { setDraggingId(null); return; }

        const from = data.findIndex(d => d.id === draggingId);
        const to   = data.findIndex(d => d.id === targetId);
        const next = [...data];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setData(next);
        setDraggingId(null);

        try {
            if (selectedCategoryId) {
                const sortList = next.map((item, i) => ({ id: item.id, category_sort_order: i + 1 }));
                await axiosPrivate.put('gift-image/sort-alignment-by-category', {
                    category_id: selectedCategoryId,
                    sort_list: sortList,
                });
            } else {
                const sortList = next.map((item, i) => ({ id: item.id, sort_order: (page - 1) * size + i + 1 }));
                await axiosPrivate.put('gift-image/sort-alignment', { sort_list: sortList });
            }
        } catch {}
    }, [draggingId, data, page, size, selectedCategoryId, axiosPrivate]);

    const handleDragEnd = useCallback(() => {
        setDraggingId(null);
        setDragOverId(null);
    }, []);

    // ── Position input ──────────────────────────────────────────
    const handlePositionClick = useCallback((id, index) => {
        const effectiveSize = size === 0 ? totalRecords : size;
        setPositionInput(String((page - 1) * effectiveSize + index + 1));
        setEditingPositionId(id);
    }, [page, size, totalRecords]);

    const applyPositionMove = useCallback(async (id, currentIndex) => {
        setEditingPositionId(null);
        const newAbsolute = parseInt(positionInput, 10);
        const effectiveSize = size === 0 ? totalRecords : size;
        const currentAbsolute = (page - 1) * effectiveSize + currentIndex + 1;
        if (isNaN(newAbsolute) || newAbsolute < 1 || newAbsolute === currentAbsolute) return;

        // Convert absolute target to page-relative index
        const pageStart = (page - 1) * effectiveSize + 1;
        const pageEnd   = pageStart + data.length - 1;
        const clampedAbsolute = Math.max(pageStart, Math.min(pageEnd, newAbsolute));
        const toIndex = clampedAbsolute - pageStart;

        const next = [...data];
        const [moved] = next.splice(currentIndex, 1);
        next.splice(toIndex, 0, moved);
        setData(next);

        try {
            if (selectedCategoryId) {
                const sortList = next.map((item, i) => ({ id: item.id, category_sort_order: i + 1 }));
                await axiosPrivate.put('gift-image/sort-alignment-by-category', {
                    category_id: selectedCategoryId,
                    sort_list: sortList,
                });
            } else {
                const sortList = next.map((item, i) => ({ id: item.id, sort_order: (page - 1) * effectiveSize + i + 1 }));
                await axiosPrivate.put('gift-image/sort-alignment', { sort_list: sortList });
            }
        } catch {}
    }, [positionInput, data, page, size, totalRecords, selectedCategoryId, axiosPrivate]);

    // ── Pagination helpers ──────────────────────────────────────
    const buildPageRange = () => {
        const set = new Set([1, lastPage]);
        for (let i = Math.max(1, page - 1); i <= Math.min(lastPage, page + 1); i++) set.add(i);
        return [...set].sort((a, b) => a - b);
    };

    const showingFrom = totalRecords === 0 ? 0 : size === 0 ? 1 : (page - 1) * size + 1;
    const showingTo   = size === 0 ? totalRecords : Math.min(page * size, totalRecords);
    const pageRange   = buildPageRange();

    // ── Tag chips helper ────────────────────────────────────────
    const renderTags = (row) => {
        if (!row.tags) return null;
        return row.tags.split(',').filter(Boolean).map(s => s.trim()).map(slug => {
            const cat = categories.find(c => c.slug === slug);
            const label = cat ? (selectedLanguage === 'da' ? cat.name_da : cat.name_en) : slug;
            return <TagChip key={slug} slug={slug} label={label} />;
        });
    };

    return (
        <>
            <Navbar
                title="Gift Cards"
                btnTitle="Add New Gift Card"
                btnLink="/admin/dashboard/gift-cards/create"
            />

            <div className="gci-page">
                {/* ── Toolbar ─────────────────────────────────── */}
                <div className="gci-toolbar">
                    <div className="gci-toolbar-right">
                        <div className="gci-toolbar-status">
                            {recentlyViewed && (
                                <button className="gci-rv-jump-btn" onClick={handleScrollToRecentlyViewed} title={`Jump to: ${recentlyViewed.title}`}>
                                    <i className="fa-regular fa-clock"></i>
                                    Recently viewed
                                    <button
                                        className="gci-rv-jump-dismiss"
                                        onClick={e => { e.stopPropagation(); handleDismissRecentlyViewed(); }}
                                        title="Dismiss"
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </button>
                            )}

                            {lastEdited && (
                                <button className="gci-le-jump-btn" onClick={handleScrollToLastEdited} title={`Jump to last edit: ${lastEdited.title}`}>
                                    <i className="fa-solid fa-pen-to-square"></i>
                                    Last edited
                                    <button
                                        className="gci-le-jump-dismiss"
                                        onClick={e => { e.stopPropagation(); handleClearHistory(); }}
                                        title="Clear"
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </button>
                            )}

                            {editedIds.size > 0 && (
                                <span className="gci-edited-count-badge" title="Cards edited this session">
                                    <i className="fa-solid fa-check-circle" style={{ color: '#4A9B6F', fontSize: '0.7rem' }}></i>
                                    <strong>{editedIds.size}</strong> edited
                                    <button
                                        className="gci-edited-count-clear"
                                        onClick={handleClearHistory}
                                        title="Clear edit history"
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </span>
                            )}
                        </div>

                        <div className="gci-toolbar-controls">
                            <div className="gci-lang-toggle">
                                <button
                                    className={`gci-lang-btn${selectedLanguage === 'en' ? ' active' : ''}`}
                                    onClick={() => { setSelectedLanguage('en'); setPage(1); }}
                                >EN</button>
                                <button
                                    className={`gci-lang-btn${selectedLanguage === 'da' ? ' active' : ''}`}
                                    onClick={() => { setSelectedLanguage('da'); setPage(1); }}
                                >DA</button>
                            </div>

                            {totalRecords > 0 && (
                                <span className="gci-count-badge">
                                    <strong>{totalRecords}</strong> cards
                                </span>
                            )}

                            <select
                                className="gci-size-select"
                                value={size}
                                onChange={e => { setSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value={10}>10 / page</option>
                                <option value={20}>20 / page</option>
                                <option value={50}>50 / page</option>
                                <option value={0}>All</option>
                            </select>
                        </div>
                    </div>

                    <div className="gci-toolbar-left">
                        <div className="gci-filter-chips">
                            {categories.map(cat => {
                                const isActive = tagFilter === cat.slug;
                                const c = slugColor(cat.slug);
                                return (
                                    <button
                                        key={cat.id}
                                        className={[
                                            'gci-filter-chip',
                                            isActive ? 'active' : '',
                                            !cat.is_active ? 'inactive-cat' : '',
                                        ].filter(Boolean).join(' ')}
                                        style={isActive ? {
                                            background: c.bg,
                                            color: c.text,
                                            borderColor: c.border,
                                        } : {}}
                                        onClick={() => handleTagFilter(cat.slug)}
                                        title={!cat.is_active ? 'Inactive category' : undefined}
                                    >
                                        {!cat.is_active && (
                                            <span className="gci-chip-inactive-dot" />
                                        )}
                                        {selectedLanguage === 'da' ? cat.name_da : cat.name_en}
                                        {isActive && (
                                            <i className="fa-solid fa-xmark" style={{ fontSize: '0.6rem', marginLeft: 4 }}></i>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* ── Progress bar (page/filter changes) ───────── */}
                {isFetching && !isLoading && (
                    <div className="gci-progress-bar">
                        <div className="gci-progress-fill" />
                    </div>
                )}

                {/* ── Initial load spinner ──────────────────────── */}
                {isLoading && (
                    <div className="gci-loading">
                        <div className="gci-spinner" />
                        <span className="gci-loading-text">Loading gift cards…</span>
                    </div>
                )}

                {/* ── Empty ────────────────────────────────────── */}
                {!isLoading && data.length === 0 && (
                    <div className="gci-empty">
                        <div className="gci-empty-icon">
                            <i className="fa-regular fa-gift"></i>
                        </div>
                        <h3>No gift cards found</h3>
                        <p>Try a different tag filter, or{' '}
                            <Link to="/admin/dashboard/gift-cards/create" style={{ color: '#9c6eaa' }}>
                                add a new card
                            </Link>.
                        </p>
                    </div>
                )}

                {/* ── Card grid ────────────────────────────────── */}
                {!isLoading && data.length > 0 && (
                    <div className={`gci-grid${isFetching ? ' gci-grid-fetching' : ''}`}>
                        {data.map((row, index) => (
                            <div
                                key={row.id}
                                className={[
                                    'gci-card',
                                    `lang-${row.language_code}`,
                                    draggingId === row.id  ? 'gci-dragging'  : '',
                                    dragOverId === row.id && draggingId !== row.id ? 'gci-drag-over' : '',
                                    !row.is_active ? 'gci-inactive' : '',
                                    editedIds.has(row.id) ? 'gci-recently-edited' : '',
                                ].filter(Boolean).join(' ')}
                                data-card-id={row.id}
                                style={{ animationDelay: `${Math.min(index * 0.04, 0.5)}s` }}
                                draggable
                                onDragStart={e => handleDragStart(e, row.id)}
                                onDragOver={e  => handleDragOver(e, row.id)}
                                onDrop={e      => handleDrop(e, row.id)}
                                onDragEnd={handleDragEnd}
                                onDragLeave={() => setDragOverId(null)}
                            >
                                {/* Image */}
                                <div className="gci-card-img-wrap">
                                    {row.image
                                        ? <img className="gci-card-img" src={row.image} alt={row.title} loading="lazy" />
                                        : <div className="gci-card-img-placeholder"><i className="fa-regular fa-gift"></i></div>
                                    }
                                    <span className="gci-lang-badge">{row.language_code?.toUpperCase()}</span>
                                    <span className="gci-drag-handle" title="Drag to reorder">
                                        <i className="fa-solid fa-grip-vertical"></i>
                                    </span>
                                    {editedIds.has(row.id) && (
                                        <span className="gci-edited-badge" title="Edited this session">
                                            <i className="fa-solid fa-check"></i> Edited
                                        </span>
                                    )}
                                    {recentlyViewed?.id === row.id && (
                                        <span className="gci-rv-badge" title="Recently viewed">
                                            <i className="fa-regular fa-clock"></i> Viewed
                                        </span>
                                    )}
                                    {/* Position badge — click to type new position */}
                                    <span
                                        className={`gci-pos-badge${editingPositionId === row.id ? ' editing' : ''}`}
                                        title="Click to move to a specific position"
                                        onClick={e => { e.stopPropagation(); handlePositionClick(row.id, index); }}
                                    >
                                        {editingPositionId === row.id ? (
                                            <input
                                                className="gci-pos-input"
                                                type="number"
                                                value={positionInput}
                                                autoFocus
                                                onChange={e => setPositionInput(e.target.value)}
                                                onBlur={() => applyPositionMove(row.id, index)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') applyPositionMove(row.id, index);
                                                    if (e.key === 'Escape') setEditingPositionId(null);
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                        ) : (
                                            <>#{(page - 1) * (size === 0 ? totalRecords : size) + index + 1}</>
                                        )}
                                    </span>
                                </div>

                                {/* Body */}
                                <div className="gci-card-body">
                                    <h3 className="gci-card-title" title={row.title}>{row.title}</h3>

                                    <div className="gci-tags">
                                        {renderTags(row)}
                                    </div>

                                    <div className="gci-stats">
                                        <div className="gci-stat">
                                            <span className="gci-stat-label">Price</span>
                                            <span className="gci-stat-value">{row.price}</span>
                                        </div>
                                        <div className="gci-stat">
                                            <span className="gci-stat-label">Uses</span>
                                            <span className="gci-stat-value">{row.count ?? 0}</span>
                                        </div>
                                    </div>

                                    <div className="gci-card-actions">
                                        <StatusSwitch
                                            checked={!!row.is_active}
                                            onChange={e => handleToggleStatus(row.id, e.target.checked)}
                                        />
                                        <div className="gci-action-btns">
                                            <Link
                                                to={`/admin/dashboard/gift-cards/edit/${row.id}`}
                                                className="gci-edit-btn"
                                                title="Edit"
                                                onClick={() => handleEditClick(row)}
                                            >
                                                <i className="fa-regular fa-pen-to-square"></i>
                                            </Link>
                                            <button
                                                className="gci-delete-btn"
                                                onClick={() => handleDelete(row.id)}
                                                title="Delete"
                                            >
                                                <i className="fa-solid fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── All-mode count bar ───────────────────────── */}
                {totalRecords > 0 && !isLoading && size === 0 && (
                    <div className="gci-footer">
                        <span className="gci-showing">
                            Showing all <strong>{totalRecords}</strong> cards
                        </span>
                    </div>
                )}

                {/* ── Floating scroll buttons (All mode) ───────── */}
                {size === 0 && !isLoading && (
                    <div className="gci-scroll-fab">
                        <button
                            className="gci-scroll-btn"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            title="Scroll to top"
                        >
                            <i className="fa-solid fa-chevron-up"></i>
                        </button>
                        <button
                            className="gci-scroll-btn"
                            onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                            title="Scroll to bottom"
                        >
                            <i className="fa-solid fa-chevron-down"></i>
                        </button>
                    </div>
                )}

                {/* ── Pagination ───────────────────────────────── */}
                {totalRecords > 0 && !isLoading && size !== 0 && (
                    <div className="gci-footer">
                        <span className="gci-showing">
                            Showing <strong>{showingFrom}–{showingTo}</strong> of <strong>{totalRecords}</strong> cards
                        </span>

                        <div className="gci-pagination">
                            <button
                                className="gci-page-btn"
                                onClick={() => setPage(p => p - 1)}
                                disabled={page === 1}
                                title="Previous page"
                            >
                                <i className="fa-solid fa-chevron-left"></i>
                            </button>

                            {pageRange.map((p, i) => (
                                <React.Fragment key={p}>
                                    {i > 0 && pageRange[i] - pageRange[i - 1] > 1 && (
                                        <span className="gci-page-ellipsis">···</span>
                                    )}
                                    <button
                                        className={`gci-page-btn${p === page ? ' active' : ''}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                </React.Fragment>
                            ))}

                            <button
                                className="gci-page-btn"
                                onClick={() => setPage(p => p + 1)}
                                disabled={page === lastPage}
                                title="Next page"
                            >
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}